import type { Manifest, Rect, TextRun, TextContent, SearchHit, SearchOptions } from "@bdfkit/core";
import type { HitRect } from "./search.js";
import type { WorkerCall, WorkerResponse, OpenSource, WorkerErrorCode, WorkerOpenOptions, RasterizeRequest, RasterizeResponse, PlayData, AudioData } from "./protocol.js";
import { domSvgRasterizer } from "./svg.js";

/** An error from the worker; code says when the document needs a password. */
export class BdfWorkerError extends Error {
  constructor(message: string, readonly code?: WorkerErrorCode) {
    super(message);
    this.name = "BdfWorkerError";
  }
}

/**
 * Main-thread handle to a rendering worker. It also draws the SVG images
 * the worker asks for, which workers cannot decode.
 */
export class BdfWorkerClient {
  private next = 1;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  private svg = domSvgRasterizer();

  constructor(readonly worker: Worker) {
    worker.onmessage = (ev: MessageEvent<WorkerResponse | RasterizeRequest>) => {
      if ("type" in ev.data && ev.data.type === "rasterize") {
        this.rasterize(ev.data);
        return;
      }
      const res = ev.data as WorkerResponse;
      const p = this.pending.get(res.id);
      if (!p) return;
      this.pending.delete(res.id);
      if (res.ok) p.resolve(res.result); else p.reject(new BdfWorkerError(res.error, res.code));
    };
  }

  private async rasterize(req: RasterizeRequest): Promise<void> {
    let res: RasterizeResponse;
    try {
      if (!this.svg) throw new Error("no DOM to draw SVG images with");
      res = { rid: req.rid, ok: true, bitmap: await this.svg(req.hash, req.data, req.width, req.height) };
    } catch (e) {
      res = { rid: req.rid, ok: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.worker.postMessage(res, res.ok ? [res.bitmap] : []);
  }

  private call<T>(req: WorkerCall, transfer: Transferable[] = []): Promise<T> {
    const id = this.next++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker.postMessage({ id, ...req }, transfer);
    });
  }

  /**
   * Open a document. An encrypted one rejects with a BdfWorkerError whose
   * code is "password-required" (no password given) or "wrong-password";
   * the worker keeps it, so unlock() can try other passwords.
   */
  open(source: OpenSource, password?: string, options?: WorkerOpenOptions): Promise<Manifest> {
    return this.call<Manifest>({ type: "open", source, password, options }, source.kind === "buffer" ? [source.buffer] : []);
  }
  /** Try a password on the encrypted document the last open() left locked. */
  unlock(password: string): Promise<Manifest> {
    return this.call<Manifest>({ type: "unlock", password });
  }
  /**
   * Put a page that a streamed conversion returned (a single-file bdf whose
   * only page is that page, with the parts it brings) in place of page
   * index of a view of the open document. The buffer is transferred.
   */
  addPage(view: string, page: number, buffer: ArrayBuffer): Promise<null> {
    return this.call<null>({ type: "addPage", view, page, buffer }, [buffer]);
  }
  /**
   * Swap in another document with the same views and pages, such as the
   * finished conversion of a streamed document; requests already running
   * finish on the old one.
   */
  replace(source: OpenSource): Promise<Manifest> {
    return this.call<Manifest>({ type: "replace", source }, source.kind === "buffer" ? [source.buffer] : []);
  }
  page(view: string, page: number, scale: number, roles?: string[]): Promise<ImageBitmap> {
    return this.call<ImageBitmap>({ type: "page", view, page, scale, roles });
  }
  continuous(view: string, viewport: Rect, scale: number): Promise<ImageBitmap> {
    return this.call<ImageBitmap>({ type: "continuous", view, viewport, scale });
  }
  sheet(view: string, viewport: Rect, scale: number): Promise<ImageBitmap> {
    return this.call<ImageBitmap>({ type: "sheet", view, viewport, scale });
  }
  /** Text runs of a page in page units; advances are measured with the embedded fonts. */
  text(view: string, page: number): Promise<TextRun[]> {
    return this.call<TextRun[]>({ type: "text", view, page });
  }
  /** Text runs of the continuous layout inside viewport, in viewport coordinates. */
  continuousText(view: string, viewport: Rect): Promise<TextRun[]> {
    return this.call<TextRun[]>({ type: "continuousText", view, viewport });
  }
  /** Runs of a page with its structure and links (for accessible text layers), in page units. */
  content(view: string, page: number): Promise<TextContent> {
    return this.call<TextContent>({ type: "content", view, page });
  }
  /** Content of the continuous layout inside viewport, in viewport coordinates. */
  continuousContent(view: string, viewport: Rect): Promise<TextContent> {
    return this.call<TextContent>({ type: "continuousContent", view, viewport });
  }
  /**
   * Content of the sheet tiles that intersect viewport (or any of several
   * rectangles: the frozen panes and the scrolled region), in sheet
   * coordinates.
   */
  sheetContent(view: string, viewport: Rect | Rect[]): Promise<TextContent> {
    return this.call<TextContent>({ type: "sheetContent", view, viewport });
  }
  search(view: string, query: string, options?: SearchOptions): Promise<SearchHit[]> {
    return this.call<SearchHit[]>({ type: "search", view, query, options });
  }
  /** Rectangles for each hit, in page or sheet coordinates. */
  locate(view: string, hits: SearchHit[]): Promise<HitRect[][]> {
    return this.call<HitRect[][]>({ type: "locate", view, hits });
  }
  /**
   * The music of a view (spec §4.4): its Standard MIDI File (for
   * MusicPlayer) and its cues; null when the view plays nothing.
   */
  play(view: string): Promise<PlayData | null> {
    return this.call<PlayData | null>({ type: "play", view });
  }
  /**
   * The recording of a view (spec §4.4): the audio file as a blob (for
   * AudioPlayer) and its cues; null when the view plays none.
   */
  audio(view: string): Promise<AudioData | null> {
    return this.call<AudioData | null>({ type: "audio", view });
  }
  close(): Promise<null> {
    return this.call<null>({ type: "close" });
  }
  terminate(): void {
    this.worker.terminate();
  }
}
